import React, { useMemo } from 'react';
import { TimeEntry } from '../../services/api';
import {
  addDays,
  getLocalDateString,
  startOfMonthGrid,
  endOfMonthGrid,
  startOfMonth,
  endOfMonth,
} from '../../utils/dateHelpers';

interface MonthViewProps {
  monthRefDate: Date;                            // Any date in the target month
  entriesByDate: Map<string, TimeEntry[]>;       // YYYY-MM-DD → entries (for the full visible grid)
  todayIso: string;                              // local-today YYYY-MM-DD
  onSelectDate: (date: string) => void;          // Click a cell → switch to Day view on that date
  isLoading: boolean;
  maxEntriesShown?: number;                      // Entries to show per cell before "+N more"; default 2
}

const WEEKDAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const sumHours = (entries: TimeEntry[]): number =>
  entries.reduce((total, e) => total + Number(e.hours || 0), 0);

export const MonthView: React.FC<MonthViewProps> = ({
  monthRefDate,
  entriesByDate,
  todayIso,
  onSelectDate,
  isLoading,
  maxEntriesShown = 2,
}) => {
  // Cell model for all 35/42 visible cells.
  const cells = useMemo(() => {
    const gridStart = startOfMonthGrid(monthRefDate);
    const gridEnd = endOfMonthGrid(monthRefDate);
    const monthStart = startOfMonth(monthRefDate);
    const monthEnd = endOfMonth(monthRefDate);
    const out: Array<{
      iso: string;
      date: Date;
      dayNum: number;
      inMonth: boolean;
      isToday: boolean;
      entries: TimeEntry[];
    }> = [];
    let cur = new Date(gridStart);
    while (cur <= gridEnd) {
      const iso = getLocalDateString(cur);
      out.push({
        iso,
        date: new Date(cur),
        dayNum: cur.getDate(),
        inMonth: cur >= monthStart && cur <= monthEnd,
        isToday: iso === todayIso,
        entries: entriesByDate.get(iso) || [],
      });
      cur = addDays(cur, 1);
    }
    return out;
  }, [monthRefDate, entriesByDate, todayIso]);

  const monthTotal = useMemo(
    () => cells.filter((c) => c.inMonth).reduce((sum, c) => sum + sumHours(c.entries), 0),
    [cells]
  );

  return (
    <div className="space-y-3">
      {/* Month total */}
      <div className="flex justify-end items-center">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          Month total:
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            {monthTotal.toFixed(2)}h
          </span>
        </span>
      </div>

      {isLoading && (
        <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-2">
          Loading…
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Weekday header row */}
        <div className="grid grid-cols-7 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          {WEEKDAY_HEADERS.map((w) => (
            <div
              key={w}
              className="px-2 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide"
            >
              {w}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {cells.map((cell) => {
            const total = sumHours(cell.entries);
            const hasEntries = cell.entries.length > 0;
            const visible = cell.entries.slice(0, maxEntriesShown);
            const hidden = Math.max(0, cell.entries.length - visible.length);

            return (
              <button
                type="button"
                key={cell.iso}
                onClick={() => onSelectDate(cell.iso)}
                className={
                  'relative h-24 sm:h-28 text-left p-1.5 border-b border-r border-gray-100 dark:border-gray-700/70 flex flex-col gap-1 transition-colors ' +
                  (cell.inMonth
                    ? 'bg-white dark:bg-gray-800 hover:bg-green-50 dark:hover:bg-green-900/10'
                    : 'bg-gray-50 dark:bg-gray-900/30 text-gray-400 dark:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-900/50') +
                  (cell.isToday ? ' ring-1 ring-inset ring-green-500 dark:ring-green-600' : '')
                }
              >
                {/* Header row: day number + daily total pill */}
                <div className="flex items-start justify-between gap-1">
                  <span
                    className={
                      'text-xs font-semibold ' +
                      (cell.isToday
                        ? 'text-green-700 dark:text-green-400'
                        : cell.inMonth
                        ? 'text-gray-700 dark:text-gray-200'
                        : 'text-gray-400 dark:text-gray-600')
                    }
                  >
                    {cell.dayNum}
                  </span>
                  {hasEntries && (
                    <span
                      className={
                        'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ' +
                        (cell.inMonth
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400')
                      }
                    >
                      {total.toFixed(total >= 10 ? 1 : 2)}h
                    </span>
                  )}
                </div>

                {/* Entry preview lines (hidden on very narrow viewports) */}
                <div className="hidden sm:flex flex-col gap-0.5 overflow-hidden">
                  {visible.map((entry) => (
                    <div
                      key={entry.id}
                      className={
                        'flex items-center gap-1 text-[11px] leading-tight truncate ' +
                        (cell.inMonth
                          ? 'text-gray-600 dark:text-gray-300'
                          : 'text-gray-400 dark:text-gray-600')
                      }
                      title={`${entry.description} · ${Number(entry.hours).toFixed(2)}h`}
                    >
                      <span className="flex-shrink-0 w-1 h-1 rounded-full bg-green-500 dark:bg-green-400" />
                      <span className="truncate">{entry.description}</span>
                      <span className="ml-auto flex-shrink-0 font-medium">
                        {Number(entry.hours).toFixed(total >= 10 ? 1 : 2)}h
                      </span>
                    </div>
                  ))}
                  {hidden > 0 && (
                    <div className="text-[10px] text-gray-500 dark:text-gray-400 italic">
                      +{hidden} more
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Tip: click any day to open its full detail in Day view.
      </p>
    </div>
  );
};
